/**
 * Zoom/Pan Gesture Detection
 *
 * Handles wheel events and trackpad gestures, detecting device type
 * and emitting appropriate zoom/pan events.
 */

import type { Platform, Browser, ZoomConfig, PanConfig, InputSource } from './types';
import {
	type HeuristicsManager,
	type BufferedWheelEvent,
	createHeuristicsManager,
	analyzeWheelEvents,
	determineDeviceType,
} from './heuristics';

// ============================================================================
// DEVICE DETECTION
// ============================================================================

export type DeviceType = 'mouse' | 'trackpad' | 'unknown';

export interface WheelEventData {
	deltaX: number;
	deltaY: number;
	ctrlKey: boolean;
	deltaMode?: number;
	timestamp: number;
}

export interface DeviceDetector {
	/** Process a wheel event and return the determined device type */
	processEvent(event: WheelEventData): DeviceType;

	/** Reset detection (e.g., after idle timeout) */
	reset(): void;

	/** Check if detection is buffering */
	isBuffering(): boolean;

	/** Get buffered events (for processing after detection) */
	getBufferedEvents(): WheelEventData[];

	/** Clear buffered events */
	clearBuffer(): void;

	/** Get the heuristics manager for tuning */
	getHeuristics(): HeuristicsManager;

	/** Get current cached device type (for debugging) */
	getCachedDevice(): DeviceType;

	/** Get current buffer size (for debugging) */
	getBufferSize(): number;
}

export interface DeviceDetectorOptions {
	/** Pre-configured heuristics manager (optional) */
	heuristics?: HeuristicsManager;
}

export function createDeviceDetector(
	platform: Platform = 'mac',
	browser: Browser = 'chrome',
	options?: DeviceDetectorOptions
): DeviceDetector {
	const heuristics = options?.heuristics ?? createHeuristicsManager(platform, browser);

	let cachedDevice: DeviceType = 'unknown';
	let buffer: BufferedWheelEvent[] = [];
	let lastEventTime = 0;

	function analyzeBuffer(): DeviceType {
		const thresholds = heuristics.getThresholds();

		if (buffer.length < thresholds.bufferSize) {
			return 'unknown';
		}

		const analysis = analyzeWheelEvents(buffer);
		return determineDeviceType(analysis, heuristics.getHeuristics());
	}

	return {
		processEvent(event: WheelEventData): DeviceType {
			const now = event.timestamp;
			const thresholds = heuristics.getThresholds();

			// Reset if idle too long
			if (now - lastEventTime > thresholds.idleTimeoutMs && cachedDevice !== 'unknown') {
				cachedDevice = 'unknown';
				buffer = [];
			}
			lastEventTime = now;

			// If we have a cached determination, use it
			if (cachedDevice !== 'unknown') {
				return cachedDevice;
			}

			// Buffer events for analysis
			const bufferedEvent: BufferedWheelEvent = {
				deltaX: event.deltaX,
				deltaY: event.deltaY,
				deltaMode: event.deltaMode ?? 0,
				ctrlKey: event.ctrlKey,
				timestamp: event.timestamp,
			};
			buffer.push(bufferedEvent);

			// Trim old events
			const cutoff = now - thresholds.bufferTimeMs;
			buffer = buffer.filter((e) => e.timestamp >= cutoff);

			// Try to determine device type
			const detected = analyzeBuffer();
			if (detected !== 'unknown') {
				cachedDevice = detected;
			}

			return detected;
		},

		reset(): void {
			cachedDevice = 'unknown';
			buffer = [];
			lastEventTime = 0;
		},

		isBuffering(): boolean {
			const thresholds = heuristics.getThresholds();
			return cachedDevice === 'unknown' && buffer.length > 0 && buffer.length < thresholds.bufferSize;
		},

		getBufferedEvents(): WheelEventData[] {
			return buffer.map((e) => ({
				deltaX: e.deltaX,
				deltaY: e.deltaY,
				ctrlKey: e.ctrlKey,
				deltaMode: e.deltaMode,
				timestamp: e.timestamp,
			}));
		},

		clearBuffer(): void {
			buffer = [];
		},

		getHeuristics(): HeuristicsManager {
			return heuristics;
		},

		getCachedDevice(): DeviceType {
			return cachedDevice;
		},

		getBufferSize(): number {
			return buffer.length;
		},
	};
}

// ============================================================================
// ZOOM CALCULATION
// ============================================================================

export interface ZoomResult {
	factor: number;
	origin: { x: number; y: number };
	source: InputSource;
}

export interface ZoomCalculator {
	calculate(
		deltaY: number,
		origin: { x: number; y: number },
		source: InputSource,
		scale?: number
	): ZoomResult;
	configure(config: ZoomConfig): void;
	getConfig(): ZoomConfig;
}

// Platform-specific defaults tuned for natural feel
const ZOOM_PLATFORM_DEFAULTS: Record<string, Partial<ZoomConfig>> = {
	mac: {
		mouse: { sensitivity: 0.01 }, // Mouse wheel sends large deltas (~100)
		trackpad: { sensitivity: 1.0 }, // Trackpad uses exponential: scale^sensitivity, 1.0 = direct mapping
	},
};

export function createZoomCalculator(
	platform: Platform,
	browser: Browser,
	initialConfig?: ZoomConfig
): ZoomCalculator {
	// Get platform defaults
	const platformDefaults = ZOOM_PLATFORM_DEFAULTS[platform] || {};

	let config: ZoomConfig = {
		sensitivity: 1.0,
		invertDirection: false,
		mouse: { sensitivity: 0.01, ...platformDefaults.mouse },
		trackpad: { sensitivity: 1.0, ...platformDefaults.trackpad },
		...initialConfig,
	};

	function getEffectiveConfig(): ZoomConfig {
		// Apply platform-specific overrides
		const platformKey = `${platform}:${browser}`;
		const platformOverride = config.platforms?.[platformKey] || config.platforms?.[platform];

		if (platformOverride) {
			return {
				...config,
				...platformOverride,
				mouse: { ...config.mouse, ...platformOverride.mouse },
				trackpad: { ...config.trackpad, ...platformOverride.trackpad },
			};
		}

		return config;
	}

	return {
		calculate(
			deltaY: number,
			origin: { x: number; y: number },
			source: InputSource,
			scale?: number
		): ZoomResult {
			const cfg = getEffectiveConfig();
			let factor: number;

			if (source === 'trackpad' && scale !== undefined) {
				// Direct scale from gesture events (Safari) or test harness
				// Exponential: factor = scale^sensitivity for symmetric zoom in/out
				const sensitivity = cfg.trackpad?.sensitivity ?? 1.0;
				factor = Math.pow(scale, sensitivity * (cfg.sensitivity ?? 1.0));
			} else if (source === 'trackpad') {
				// Trackpad pinch via wheel events (Chrome/Firefox on Mac)
				// Uses smaller, continuous deltaY values
				const sensitivity = cfg.trackpad?.sensitivity ?? 1.0;
				// Negate delta: pinch out (negative deltaY) = zoom in
				let adjustedDelta = -deltaY;
				if (cfg.invertDirection) {
					adjustedDelta = -adjustedDelta;
				}
				// Trackpad deltaY is typically small (-10 to 10), so use higher base
				// sensitivity=1.0 gives natural 1:1 feel
				const zoomBase = 1.01; // ~1% per unit delta
				factor = Math.pow(zoomBase, adjustedDelta * sensitivity * (cfg.sensitivity ?? 1.0));
			} else {
				// Mouse wheel zoom - use exponential formula for consistent percentage changes
				const sensitivity = cfg.mouse?.sensitivity ?? 0.01;
				// Industry standard: scroll up (negative deltaY) = zoom in, scroll down = zoom out
				// Negate delta so positive deltaY (scroll down) gives factor < 1 (zoom out)
				let adjustedDelta = -deltaY;
				if (cfg.invertDirection) {
					adjustedDelta = -adjustedDelta;
				}
				// Exponential zoom: factor = base^(delta * sensitivity)
				// This ensures each scroll tick produces a consistent percentage change
				// regardless of current zoom level (e.g., ~5% per tick for typical delta of 100)
				const zoomBase = 1.002; // ~0.2% per unit delta
				factor = Math.pow(zoomBase, adjustedDelta * sensitivity * (cfg.sensitivity ?? 1.0) * 100);
			}

			// Clamp factor to reasonable bounds
			factor = Math.max(0.1, Math.min(10, factor));

			return { factor, origin, source };
		},

		configure(newConfig: ZoomConfig): void {
			config = {
				...config,
				...newConfig,
				mouse: { ...config.mouse, ...newConfig.mouse },
				trackpad: { ...config.trackpad, ...newConfig.trackpad },
				platforms: { ...config.platforms, ...newConfig.platforms },
			};
		},

		getConfig(): ZoomConfig {
			return { ...config };
		},
	};
}

// ============================================================================
// PAN CALCULATION
// ============================================================================

export interface PanResult {
	delta: { x: number; y: number };
	source: InputSource;
	inertial: boolean;
}

export interface PanCalculator {
	calculate(
		deltaX: number,
		deltaY: number,
		source: InputSource,
		inertial?: boolean
	): PanResult;
	configure(config: PanConfig): void;
	getConfig(): PanConfig;
}

// Platform-specific pan defaults tuned for natural feel
const PAN_PLATFORM_DEFAULTS: Record<string, Partial<PanConfig>> = {
	mac: {
		mouse: { sensitivity: 1.0 }, // MMB drag feels natural at 1.0
		trackpad: { sensitivity: 1.5 }, // Trackpad scroll needs slightly higher sensitivity
	},
};

export function createPanCalculator(
	platform: Platform,
	browser: Browser,
	initialConfig?: PanConfig
): PanCalculator {
	// Get platform defaults
	const platformDefaults = PAN_PLATFORM_DEFAULTS[platform] || {};

	let config: PanConfig = {
		sensitivity: 1.0,
		invertX: false,
		invertY: false,
		// Mouse: no inversion by default, but don't set explicit values so global config can override
		mouse: { sensitivity: 1.0, ...platformDefaults.mouse },
		// Trackpad: invert both axes by default for "content follows fingers" behavior
		trackpad: { sensitivity: 1.5, invertX: true, invertY: true, ...platformDefaults.trackpad },
		...initialConfig,
	};

	function getEffectiveConfig(): PanConfig {
		const platformKey = `${platform}:${browser}`;
		const platformOverride = config.platforms?.[platformKey] || config.platforms?.[platform];

		if (platformOverride) {
			return {
				...config,
				...platformOverride,
				mouse: { ...config.mouse, ...platformOverride.mouse },
				trackpad: { ...config.trackpad, ...platformOverride.trackpad },
			};
		}

		return config;
	}

	return {
		calculate(
			deltaX: number,
			deltaY: number,
			source: InputSource,
			inertial = false
		): PanResult {
			const cfg = getEffectiveConfig();

			// Get source-specific config
			const sourceConfig = source === 'mouse' ? cfg.mouse : cfg.trackpad;
			const sourceSensitivity = sourceConfig?.sensitivity ?? (source === 'mouse' ? 1.0 : 1.5);

			let x = deltaX * (cfg.sensitivity ?? 1.0) * sourceSensitivity;
			let y = deltaY * (cfg.sensitivity ?? 1.0) * sourceSensitivity;

			// Use source-specific invert if defined, otherwise fall back to global
			const invertX = sourceConfig?.invertX ?? cfg.invertX ?? false;
			const invertY = sourceConfig?.invertY ?? cfg.invertY ?? false;

			if (invertX) x = -x;
			if (invertY) y = -y;

			return {
				delta: { x, y },
				source,
				// Mouse drag doesn't have inertia, only trackpad scroll does
				inertial: source === 'mouse' ? false : inertial,
			};
		},

		configure(newConfig: PanConfig): void {
			config = {
				...config,
				...newConfig,
				mouse: { ...config.mouse, ...newConfig.mouse },
				trackpad: { ...config.trackpad, ...newConfig.trackpad },
				platforms: { ...config.platforms, ...newConfig.platforms },
			};
		},

		getConfig(): PanConfig {
			return { ...config };
		},
	};
}
